import { useMutation } from '@apollo/client'
import { PublisherInterface } from '@chili-publish/publisher-interface'
import { useState } from 'react'
import toast from 'react-hot-toast'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'

import { SAVE_PAGE_PREVIEW } from 'data/layoutcreator/mutations/documents'
import { SAVE_DOCUMENT } from 'data/layoutcreator/mutations/projects'
import { GET_PROJECT_DOCUMENTS_PREVIEWS } from 'data/layoutcreator/queries/documents'
import ChiliDocument from 'shared/components/Tools/services/ChiliDocument'
import { fetchImageBlob, fetchImageFile } from 'shared/helpers/file'
import { getBase64ImageUrl } from 'shared/helpers/url'
import { ImageFormats } from 'shared/types/global'
import { removeSeparateSpacesTextFlow } from 'shared/utils/chili'

import { usePreviewsCache } from '../components/PageList/hooks/usePreviewsCache'

const documentApi = ChiliDocument.getInstance()

export const useEditorSave = ({
  publisherConnector,
  backPath,
  groupId,
  projectId,
  pageId
}: {
  publisherConnector: PublisherInterface | null
  backPath: string
  groupId: string
  projectId: string
  pageId: string
}) => {
  const { formatMessage } = useIntl()
  const [isSaveDocLoading, setIsSaveDocLoading] = useState(false)
  const navigate = useNavigate()
  const { cachePreview } = usePreviewsCache()

  const [savePagePreview] = useMutation(SAVE_PAGE_PREVIEW, {
    refetchQueries: [
      {
        query: GET_PROJECT_DOCUMENTS_PREVIEWS,
        variables: {
          groupId,
          projectId
        }
      }
    ]
  })

  const [saveDocument] = useMutation(SAVE_DOCUMENT)

  // TODO: Maybe there is a way to prevent this issue on text editing
  const fixTextFrames = async () => {
    if (!publisherConnector) {
      return 0
    }

    const frames = (await publisherConnector.getObject(
      'document.pages[0].frames'
    )) as any[]

    const framesCount = frames ? Number(frames.length) : 0

    let counter = 0
    for (let i = 0; i < framesCount; i++) {
      const frame = (await publisherConnector.getObject(
        `document.pages[0].frames[${i}]`
      )) as any
      const composerSelector = `document.pages[0].frames[${frame.id}].textFlow.composer`

      if (frame.type === 'text') {
        const { textFlow } = (await publisherConnector.getObject(
          composerSelector
        )) as { textFlow: string }

        const { newTextFlow, wasModified } =
          removeSeparateSpacesTextFlow(textFlow)

        if (!wasModified) {
          continue
        }

        await publisherConnector.executeFunction(
          frame.javaScriptDescriptor,
          'ImportTextFlow',
          newTextFlow
        )

        counter++
      }
    }

    return counter
  }

  const unselectFrames = async () =>
    publisherConnector?.executeFunction(
      'document.selectedFrames',
      'Clear'
    )

  const handleSave = async ({
    silent = false
  }: { silent?: boolean } = {}) => {
    let savingDocToast: string | null = null
    if (!silent) {
      savingDocToast = toast.loading(
        formatMessage({ id: 'Toasts.saving.document' })
      )
    }

    try {
      const fixedTextFramesCount = await fixTextFrames()
      console.log(
        `%c${fixedTextFramesCount} text frames modified`,
        'color: green'
      )

      await saveDoc()
      await unselectFrames()
      await savePreview()

      if (!silent) {
        navigate(backPath)
      }
    } catch (error) {
      console.error('Error saving document', error)

      throw error
    } finally {
      if (savingDocToast) {
        toast.remove(savingDocToast)
      }
    }
  }

  const savePreview = async () => {
    const base64 = await documentApi.getSnapshot()

    const blob = await fetchImageBlob(
      `data:image/png;base64,${base64}`
    )

    if (blob) {
      cachePreview(pageId, blob)
    }

    const url = getBase64ImageUrl(base64, ImageFormats.png)

    if (url) {
      await savePagePreview({
        variables: {
          groupId,
          projectId,
          pageId,
          image: await fetchImageFile(url)
        }
      })
    }
  }

  const saveDoc = async () => {
    const isDirty = await publisherConnector?.getDirtyState()
    if (!isDirty) {
      return false
    }

    const tempXml = (await publisherConnector?.executeFunction(
      'document',
      'GetTempXML'
    )) as string

    if (pageId) {
      setIsSaveDocLoading(true)
      const resp = await saveDocument({
        variables: {
          projectId,
          documentId: pageId,
          xml: tempXml
        }
      })
      setIsSaveDocLoading(false)
      return resp.data ? resp.data.saveDocument : false
    }
    return false
  }

  return {
    handleSave,
    isLoading: isSaveDocLoading
  }
}
